import React, { FunctionComponent, useEffect } from "react";
import { useForm } from "react-hook-form";
import { Form, FormActions, FormDropdown, FormFields, Stack, mapToDropdownOption } from "sprint-common-web";

import { FormDistributorSearchDropdown } from "../../../../../common/components/form/FormDistributorDropdown";
import { mapEnumToDepascalizedDropdownOptions } from "../../../../../common/utils/mapEnumToDropdownOptions";
import { WatermarkRulePosition } from "../../../../common/types/WatermarkRulePosition";
import { WatermarkRuleItem } from "../WatermarkRuleItem";
import { useWatermarkRuleForm } from "./useWatermarkRuleForm";

export interface WatermarkRuleFormProps {
    onCancel(): void;
    onSubmit(): void;
    item?: WatermarkRuleItem;
}

export interface WatermarkRuleFormInputs {
    value: string;
    airlineId: string;
    distributorId: string;
    position: string;
}

export const WatermarkRuleForm: FunctionComponent<WatermarkRuleFormProps> = ({
    onCancel,
    onSubmit,
    item
}: WatermarkRuleFormProps) => {
    const { errors, control, formState, reset, handleSubmit, setValue, watch } = useForm<WatermarkRuleFormInputs>({
        defaultValues: {
            value: item?.value ?? "",
            airlineId: "",
            distributorId: item?.distributor.id ?? "",
            position: String(item?.position) || ""
        },
        criteriaMode: "all"
    });
    const {
        createWatermarkRule,
        updateWatermarkRule,
        validateDistributor,
        isFormLoading,
        airlines
    } = useWatermarkRuleForm({
        item,
        onSubmit: () => {
            onSubmit();
            reset();
        }
    });

    useEffect(() => {
        if (item) {
            const airline = airlines.find((a) => a.code === item.value || a.name === item.value);
            setValue("airlineId", airline?.id);
        }
    }, [item]);

    const handleCreate = async (inputs: WatermarkRuleFormInputs) => {
        if (item) {
            await updateWatermarkRule({ ...inputs, ruleId: item.id });
        } else {
            await createWatermarkRule(inputs);
        }
    };

    const handleCancel = () => {
        reset();
        onCancel();
    };

    const airlineId = watch("airlineId");
    const airline = airlines.find((a) => a.id === airlineId);

    return (
        <Form onSubmit={handleSubmit(handleCreate)}>
            <Stack verticalAlign="space-between">
                <FormFields>
                    <FormDropdown
                        options={airlines?.map((a) => mapToDropdownOption(a)) ?? []}
                        label="Airline"
                        control={control}
                        name="airlineId"
                        rules={{ required: "Search and select airline" }}
                        errors={errors}
                    />
                    <FormDropdown
                        options={
                            airline
                                ? [
                                      { key: airline.code, text: airline.code },
                                      { key: airline.name, text: airline.name }
                                  ]
                                : []
                        }
                        name="value"
                        placeholder="Value"
                        control={control}
                        label="Value to display"
                        errors={errors}
                    />

                    <FormDistributorSearchDropdown
                        initOptions={item ? [{ key: item.distributor.id, text: item.distributor.name }] : []}
                        control={control}
                        errors={errors}
                        rules={{ required: "Search and select distributor", validate: validateDistributor }}
                        name="distributorId"
                        label="Distributor"
                        placeholder={"Distributor"}
                    />

                    <FormDropdown
                        options={mapEnumToDepascalizedDropdownOptions(WatermarkRulePosition)}
                        name="position"
                        placeholder="Position"
                        control={control}
                        label="Position"
                        errors={errors}
                    />
                </FormFields>
                <FormActions
                    isLoading={isFormLoading || formState.isSubmitting || formState.isValidating}
                    onCancel={handleCancel}
                />
            </Stack>
        </Form>
    );
};
